import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '../../FrontEndReact/src/Core/Types/user';
import { getLoggedInUser } from '../../Js/React/Services/legacy';
import postInit from './postInit';
import { getCultureCode, getCurrentCultureId } from '../../Js/React/Services/common';
import { SignalRContextProvider } from '../../FrontEndReact/src/Core/Contexts/signalrContext';
import i18n from '../../FrontEndReact/src/Core/Utils/cultureInit';
import { I18nextProvider } from 'react-i18next';
import LegacyEventBridge from '../../Js/React/legacyEventBridge';
import { ICustomFrontEndConfigMap, TCustomFrontEndConfig } from '../../Js/React/Types/customFrontEnd';
import { GetCustomFrontEndConfigs } from '../../Js/React/loader';

type FrontendState = {
	user?: User;
	cultureId: number;
	cultureCode: string;
	customConfigs: TCustomFrontEndConfig[];
};

const initialState: FrontendState = {
	cultureId: getCurrentCultureId(),
	cultureCode: getCultureCode(),
	customConfigs: [],
};

const FrontendStateContext = createContext<{
	state: FrontendState;
	setState: React.Dispatch<React.SetStateAction<FrontendState>>;
	getCustomFrontEndConfig<T extends keyof ICustomFrontEndConfigMap>(configType: T): ICustomFrontEndConfigMap[T] | undefined;
}>({
	state: initialState,
	setState: () => {},
	getCustomFrontEndConfig: () => undefined,
});

type FrontendStateProviderProps = {
	children: React.ReactNode;
};

export const FrontendStateProvider = ({ children }: FrontendStateProviderProps) => {
	const [state, setState] = useState<FrontendState>(initialState);

	useEffect(() => {
		const Initialize = () => {
			const user = getLoggedInUser();
			const cultureId = getCurrentCultureId();
			const cultureCode = getCultureCode();
			setState((state) => {
				postInit();
				return {
					...state,
					user: user,
					cultureId: cultureId,
					cultureCode: cultureCode,
				};
			});

			GetCustomFrontEndConfigs().then((configs) => {
				console.log('Loaded custom frontend configs:', configs);
				setState((state) => ({
					...state,
					customConfigs: configs,
				}));
			});
		};

		Initialize();

		document.addEventListener('BizpartfrontendInitialized', Initialize);

		return () => document.removeEventListener('BizpartfrontendInitialized', Initialize);
	}, []);

	useEffect(() => {
		const normalizedCultureCode = state.cultureCode?.toLowerCase().split('-')[0] ?? 'en';
		if (i18n.language !== normalizedCultureCode) {
			i18n.changeLanguage(normalizedCultureCode);
		}
	}, [state.cultureCode]);

	const getCustomFrontEndConfig = <T extends keyof ICustomFrontEndConfigMap>(
		configType: T
	): ICustomFrontEndConfigMap[T] | undefined => {
		const config = state.customConfigs.find((c) => c.type === configType);
		return config?.config as ICustomFrontEndConfigMap[T] | undefined;
	};

	return (
		<FrontendStateContext.Provider value={{ state, setState, getCustomFrontEndConfig }}>
			{children}
		</FrontendStateContext.Provider>
	);
};

export const useFrontendState = () => {
	const context = useContext(FrontendStateContext);
	if (!context) {
		throw new Error('useFrontendState must be used within a FrontendStateProvider');
	}
	return context;
};

export const withFrontendContext = (Component: React.ReactNode) => {
	return (
		<I18nextProvider i18n={i18n}>
			<FrontendStateProvider>
				<SignalRContextProvider>
					<LegacyEventBridge>{Component}</LegacyEventBridge>
				</SignalRContextProvider>
			</FrontendStateProvider>
		</I18nextProvider>
	);
};
